import React, {FC, FormEvent, useEffect} from 'react'

import cn from 'classnames/bind'
import {observer} from 'mobx-react-lite'
import dynamic from 'next/dynamic'
import Link from 'next/link'

import FormHeader from 'components/blocks/forms/FormHeader'
import MobxCheckbox from 'components/elements/fields/MobxCheckbox'
import LoadingComponent from 'components/elements/LoadingComponent'
import {years} from 'models/Form1099/Form1099.config'
import defaultValues from 'src/components/blocks/documents/DocumentForm1099/config/templates.defaultValues'
import Form1099Table from 'src/components/blocks/forms/Form1099/Form1099Table/Form1099Table'
import FormTips from 'src/components/blocks/FormTips'
import Button from 'src/components/elements/Button'
import MobxSelect from 'src/components/elements/fields/MobxSelect'
import MobxTextInput from 'src/components/elements/fields/MobxTextInput'
import {Col, Container, Row} from 'src/components/elements/grid'
import {posthogTrack, onScrollToTop} from 'src/utils'
import {useForm1099Store} from 'src/utils/contexts/RootStore.context'
import {useToggleState} from 'src/utils/hooks/useToggleState'

import s from './Form1099.module.scss'

const DynamicForm1099Preview = dynamic(
	() => import('components/blocks/documents/DocumentForm1099/preview/Form1099Preview'),
	{loading: () => <LoadingComponent containerHeight={1500} />},
)
const cx = cn.bind(s)
interface IForm1099Props {
	isPreviewOnly?: boolean
	className?: string
}

const Form1099: FC<IForm1099Props> = ({isPreviewOnly = true, className}) => {
	const [isPreview, togglePreview] = useToggleState(false)
	const form1099 = useForm1099Store()

	useEffect(() => {
		if (isPreview) {
			posthogTrack('[1099 FORM] Document preview opened')
		}
	}, [isPreview])

	useEffect(() => {
		if (form1099.isFirstStepFilled) {
			posthogTrack('[1099 FORM] Step 1 is filled')
		}
	}, [form1099.isFirstStepFilled])

	useEffect(() => {
		if (form1099.isSecondStepFilled) {
			posthogTrack('[1099 FORM] Step 2 is filled')
		}
	}, [form1099.isSecondStepFilled])

	const data = {...form1099?.requiredFields, ...form1099?.fields}

	const onSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault()

		togglePreview()
		onScrollToTop()
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const onIncomeBlur = (_e: React.FocusEvent<HTMLInputElement>) => {
		form1099.fields.otherIncome = form1099.fields.income
	}

	const processedData = {
		payersName: data?.payersName ? data?.payersName : defaultValues.invoice.payers.companyName,
		payersAddress: data?.payersAddress ? data?.payersAddress : defaultValues.invoice.payers.address,
		payersCity: data?.payersCity ? data?.payersCity : defaultValues.invoice.payers.city,
		payersState: data?.payersState ? data?.payersState : defaultValues.invoice.payers.state,
		payersZip: data?.payersZip ? data?.payersZip : defaultValues.invoice.payers.zipCode,
		recipientsName: data?.recipientsName
			? data?.recipientsName
			: defaultValues.invoice.recipients.name,
		recipientsAddress: data?.recipientsAddress
			? data?.recipientsAddress
			: defaultValues.invoice.recipients.address,
		recipientsCity: data?.recipientsCity
			? data?.recipientsCity
			: defaultValues.invoice.recipients.city,
		recipientsState: data?.recipientsState
			? data?.recipientsState
			: defaultValues.invoice.recipients.state,
		recipientsZip: data?.recipientsZip
			? data?.recipientsZip
			: defaultValues.invoice.recipients.zipCode,
		year: data?.year,
		income: data?.income ? data?.income : defaultValues.info.income,
		corrected: data?.corrected || false,
		rents: data?.rents,
		royalties: data?.royalties,
		otherIncome: data?.otherIncome ? data?.otherIncome : data?.otherIncome,
		federalIncomeTax: data?.federalIncomeTax,
		companyEin: data?.companyEin,
		socialSecurity: data?.socialSecurity,
		fishingBoat: data?.fishingBoat,
		medicalAndHealthCare: data?.medicalAndHealthCare,
		payerDirectSales: data?.payerDirectSales,
		substitutePayments: data?.substitutePayments,
		cropInsurance: data?.cropInsurance,
		grossProceeds: data?.grossProceeds,
		field11: data?.field11,
		section409A: data?.section409A,
		accountNumber: data?.accountNumber,
		fatca: data?.fatca || false,
		excessGoldenParachutePayments: data?.excessGoldenParachutePayments,
		nonQualifiedDeferredCompensation: data?.nonQualifiedDeferredCompensation,
		stateTaxWithHeldA: data?.stateTaxWithHeldA,
		stateTaxWithHeldB: data?.stateTaxWithHeldB,
		payersStateNoA: data?.payersStateNoA,
		payersStateNoB: data?.payersStateNoB,
		stateIncomeA: data?.stateIncomeA,
		stateIncomeB: data?.stateIncomeB,
		illegalAgreement: data?.illegalAgreement,
		terms: data?.terms,
	}

	return (
		<form
			className={cx('wrapper', className, {'preview-mode': isPreview})}
			onSubmit={(e) => onSubmit(e)}>
			{isPreview ? (
				<DynamicForm1099Preview
					isPreviewOnly={isPreviewOnly}
					togglePreview={togglePreview}
					data={processedData}
				/>
			) : (
				<Container>
					<FormHeader
						title="Information"
						button={
							<Button type="submit" size="small" variant="secondary">
								Preview
							</Button>
						}
					/>
					<FormTips />
					<div className={cx('info')}>
						<Row>
							<Col md={6} xl={{offset: 1, span: 5}}>
								<MobxSelect
									wrapperClassName={cx('field')}
									placeholder="Select state"
									name="year"
									label="What year?"
									store={form1099?.fields}
									options={years}
									variant="editable"
									onChange={(e) => {
										const value = e.target.value
										form1099.fields.year = new Date(value)
									}}
									value={form1099.fields.year.getFullYear()}
								/>
							</Col>
							<Col md={6} xl={{span: 5}}>
								<MobxTextInput
									label={'How much Did You Make Before Taxes Last Year?'}
									store={form1099?.fields}
									name="income"
									required
									variant="editable"
									shouldHandleOldValue
									isNumber
									isFormatText
									onBlur={onIncomeBlur}
								/>
							</Col>
						</Row>
					</div>
					<header className={cx('header', 'header__invoice')}>
						<h2 className={cx('header__title', 'step__title')}>2. invoice</h2>
					</header>
					<Row>
						<Col xl={{offset: 1, span: 10}}>
							<Form1099Table data={form1099} />
						</Col>
					</Row>
					<Row>
						<Col md={{offset: 2, span: 8}} lg={{offset: 3, span: 6}}>
							<div className={cx('step', 'step__finish', 'finish')}>
								<h2 className={cx('step__title', 'finish__title')}>3. Finish</h2>

								<div className={cx('finish__content')}>
									<div className={cx('finish__fields')}>
										<MobxCheckbox
											label="I agree that I will not use this site for any illegal purposes"
											value={false}
											required
											name="illegalAgreement"
											store={form1099?.fields}
										/>
										<MobxCheckbox
											label={
												<>
													Please accept our <Link href="/terms">Terms of Use</Link> and{' '}
													<Link href="/privacy-policy">Privacy Policy</Link>
												</>
											}
											value={false}
											required
											name="terms"
											store={form1099?.fields}
										/>
									</div>

									<div className={cx('finish__buttons')}>
										<Button data-testid="preview" type="submit" size="big">
											Preview
										</Button>
									</div>
								</div>
							</div>
						</Col>
					</Row>
				</Container>
			)}
		</form>
	)
}

Form1099.displayName = 'Form1099'

export default observer(Form1099)
